<template>
    <div>
        <b-row>
            <b-col xs="12">
                <h2>{{buildingName}} - Positioner</h2>
            </b-col>
        </b-row>
        <Loader v-if="isLoadingParts" />

        <div v-else
             v-touch:swipe.right="onScrollInTabs"
             v-touch:swipe.left="onScrollInTabs"
             class="scrollable-tabs">
            <!--Chevron right showing if tabs container can be scrolled-->
            <div class="scrollable-icon--container" v-if="!isLoadingParts">
                <font-awesome-icon v-if="tabScrollingIcons.left"
                                   icon="chevron-left"
                                   class="text-black fa-pulse" />
                <font-awesome-icon v-if="tabScrollingIcons.right"
                                   icon="chevron-right"
                                   pulse
                                   class="text-black fa-pulse" />
            </div>

            <b-tabs ref="tabs"
                    v-model="tabIndex"
                    lazy>
                <b-tab v-for="part in parts"
                       :key="part.id">
                    <template #title>
                        {{ part.name }}
                        <b-badge variant="info">
                            {{ getNumberOfRows(part.id) }}
                        </b-badge>
                    </template>

                    <div id="drawing" style="overflow: hidden">
                        <div class="drawing--actions-buttons-group-top">
                            <div class="mb-6">
                                <b-button class="btn btn-sm btn-light btn-outline-gray-light rounded-0 text-dark  mb-1"
                                          @click="showNumber = !showNumber">
                                    <font-awesome-icon :icon="showNumber ? 'check-square' : 'square'"
                                                       class="text-info" />
                                    Visa nummer
                                </b-button>

                                <b-button-group size="sm" class="ml-1 mb-1">
                                    <b-button variant="light" class="border-gray-light" @click="changePositionScale(-0.1)" title="Mindre symboler">-</b-button>
                                    <b-button variant="light" class="border-gray-light" disabled>{{symbolSize}}</b-button>
                                    <b-button variant="light" class="border-gray-light" @click="changePositionScale(0.1)" title="Större symboler">+</b-button>
                                </b-button-group>

                                <b-form-radio-group v-model="symbolSize"
                                                    :options="scales"
                                                    size="sm"
                                                    class="ml-1 mb-1"
                                                    button-variant="light"
                                                    @change="changePositionScale"
                                                    buttons>
                                </b-form-radio-group>
                            </div>
                        </div>

                        <div class="drawing--actions-buttons-group-side">
                            <div>
                                <button class="btn btn-sm btn-light btn-outline-gray-light rounded-0"
                                        @click="onZoomIn">
                                    <font-awesome-icon icon="search-plus" class="text-info" />
                                </button>
                            </div>
                            <div>
                                <button class="btn btn-sm btn-light btn-outline-gray-light rounded-0"
                                        @click="onZoomOut">
                                    <font-awesome-icon icon="search-minus" class="text-info" />
                                </button>
                            </div>
                        </div>

                        <div style="width: 100%; height: 70vh;overflow:hidden;"
                                     v-if="!isLoadingParts">
                            <panZoom :ref="`panZoom-${part.id}`"
                                     :key="part.id"
                                     :options="{
                                         minZoom: 0.1,
                                         maxZoom: 3,
                                         smoothScroll: true,
                                         initialZoom: 0.6
                                       }"
                                     @panstart="onPanStart"
                                     @panend="onPanEnd"
                                     @init="onPanZoomInit"
                                     :style="`cursor: ${isPanning ? 'move' : 'initial'};`">
                                <svg ref="box"
                                     xmlns="http://www.w3.org/2000/svg"
                                     version="1.1"
                                     :viewBox="`0 0 ${boxSize.width} ${boxSize.height}`">
                                    <!--Drawing-->
                                    <image :xlink:href="currentPart.drawingAsBase64"
                                           height="100%"
                                           width="100%"
                                           ref="boxImage" />

                                    <g v-for="(items, key) in filteredAndGroupedPositionsByNumber"
                                       :key="key">
                                        <g v-if="items.length > 1" style="cursor:pointer;">
                                            <!--Line between icon and dot-->
                                            <line :x1="items[0].coordinateX"
                                                  :y1="items[0].coordinateY"
                                                  :x2="items[0].coordinateInfoX"
                                                  :y2="items[0].coordinateInfoY"
                                                  :stroke-width="positionSize.line"
                                                  stroke="black" />
                                            <!--Grey dot-->
                                            <circle :cx="items[0].coordinateX"
                                                    :cy="items[0].coordinateY"
                                                    :r="positionSize.radiePoint"
                                                    fill="grey" />
                                            <!--Icon-->
                                            <g :id="`popover-${items[0].id}-wrapper`"
                                               @contextmenu.prevent
                                               style="cursor: pointer;"
                                               @click.right.stop.prevent="$root.$emit('bv::show::popover', `popover-${items[0].id}`)">
                                                <!--Grouped Numbers popover-->
                                                <b-popover :id="`popover-${items[0].id}`"
                                                           :target="`popover-${items[0].id}-wrapper`"
                                                           customClass="zIndex1000 maxWidth768"
                                                           triggers="click blur">
                                                    <template #title>
                                                        <b-button @click="closeAllPopovers" class="close" style="line-height: 0.6" aria-label="Close">
                                                            <span class="d-inline-block" aria-hidden="true">&times;</span>
                                                        </b-button>
                                                        Nummer {{items[0].number}}
                                                    </template>

                                                    <div class="list-group">
                                                        <div v-for="item in items"
                                                             :key="item.id"
                                                             class="list-group-item"
                                                             role="button">
                                                            <img v-if="item.articleIcon !== null"
                                                                 style="width: 25px"
                                                                 class="mr-1"
                                                                 :src="getUrl(item.articleId)" />
                                                            {{ item.concatenatedNumbers }} - {{ item.articleName }}
                                                            {{ item.place ? '- ' + item.place : '' }}
                                                            {{ item.description ? '- ' + item.description : ''}}
                                                        </div>
                                                    </div>
                                                </b-popover>

                                                <!--Circle-->
                                                <rect :width="positionSize.width"
                                                      :height="positionSize.height"
                                                      :x="items[0].coordinateInfoX - positionSize.width/2"
                                                      :y="items[0].coordinateInfoY - positionSize.width/2"
                                                      fill="#17a2b8" />
                                                <text :x="items[0].coordinateInfoX"
                                                      :y="items[0].coordinateInfoY"
                                                      text-anchor="middle"
                                                      :font-size="items[0].number >= 100 ? positionSize.fontSizeSmall : positionSize.fontSize"
                                                      :alignment-baseline="items[0].number >= 100 ? 'middle' : 'middle'"
                                                      fill="white">
                                                    {{ items[0].number }}
                                                </text>
                                            </g>
                                        </g>

                                        <!--Icon Start-->
                                        <g v-for="item in items"
                                           v-else
                                           :key="item.id"
                                           style="cursor: pointer;"
                                           :v-b-popover="`popover-${item.id}`"
                                           :title="item.articleName"
                                           @contextmenu.prevent>
                                            <g>
                                                <!--Line between icon and dot-->
                                                <line :x1="item.coordinateX"
                                                      :y1="item.coordinateY"
                                                      :x2="item.coordinateInfoX"
                                                      :y2="item.coordinateInfoY"
                                                      :stroke-width="positionSize.line"
                                                      stroke="black" />
                                                <!--Grey dot-->
                                                <circle :cx="item.coordinateX"
                                                        :cy="item.coordinateY"
                                                        :r="positionSize.radiePoint"
                                                        fill="grey" />
                                                <!--Icon-->
                                                <g :id="`popover-${item.id}-wrapper`">
                                                    <!--Show number or icon-->
                                                    <g v-if="showNumber || !item.articleIcon">
                                                        <!--Circle-->
                                                        <circle :r="positionSize.radieCircle"
                                                                :cx="item.coordinateInfoX"
                                                                :cy="item.coordinateInfoY"
                                                                stroke="#f08000"
                                                                stroke-width="3"
                                                                fill="#f08000" />
                                                        <text :x="item.coordinateInfoX"
                                                              :y="item.coordinateInfoY "
                                                              text-anchor="middle"
                                                              :alignment-baseline="items[0].number >= 100 ? 'middle' : 'middle'"
                                                              :font-size="item.number >= 100 ? positionSize.fontSizeSmall : positionSize.fontSize"
                                                              fill="white">
                                                            {{ item.number }}
                                                        </text>
                                                    </g>
                                                    <g v-else>
                                                        <image :xlink:href="getUrl(item.articleId)"
                                                               :x="item.coordinateInfoX - positionSize.width/2"
                                                               :y="item.coordinateInfoY - positionSize.height/2"
                                                               :height="positionSize.height"
                                                               :width="positionSize.width" />
                                                    </g>
                                                    <!--Popover-->
                                                    <b-popover :id="`popover-${item.id}`"
                                                               :target="`popover-${item.id}-wrapper`"
                                                               :title="`Nummer ${item.number}`"
                                                               triggers="click blur"
                                                               customClass="zIndex1000"
                                                               style="z-index: 1000 !important">
                                                        <strong>Artikelnamn:</strong> {{ item.articleName }}<br />
                                                        <strong>Plats:</strong> {{ item.place ? '- ' + item.place : '' }} -<br />
                                                        <strong>Beskrivning:</strong> {{ item.description }}<br />
                                                    </b-popover>
                                                </g>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                            </panZoom>
                        </div>
                    </div>


                </b-tab>
            </b-tabs>
        </div>
    </div>
</template>

<script>
    import Loader from '@/components/Loader';
    import {
        isInViewport,
    } from '@/helpers/viewport.helper';
    import {
        get,
    } from '@/helpers/api';
    import {
        getDownloadUrlByArticleId,
    } from '@/helpers/file.helper';
    import {
        getPositionSizeObject,
        SCALES,
    } from '@/helpers/position-symbol.helper';

    export default {
        name: 'BuildingPositionsReadOnly',
        components: {
            Loader,
        },
        props: {
            buildingId: {
                type: Number,
                required: true
            },
            buildingName: {
                type: String,
                required: true
            }
        },
        watch: {
            isLoadingParts: {
                handler(newValue) {
                    if (!newValue)
                        this.$nextTick(() => this.onResize());
                }
            }
        },
        data() {
            return {
                isLoadingParts: true,
                tabIndex: 0,
                currentPart: null,
                tabScrollingIcons: {
                    left: false,
                    right: false
                },
                positions: [],
                parts: [],
                symbolSize: 1,
                panZoomInstance: null,
                isPanning: false,
                showNumber: false,
                boxSize: {
                    width: 500,
                    height: 400
                },
                scales: SCALES
            };
        },
        computed: {
            filteredAndGroupedPositionsByNumber() {
                return this.positions.filter(x => x.partId === this.currentPart.id).groupByKey('number');
            },
            positionSize() {
                return getPositionSizeObject(this.symbolSize);
            }
        },
        mounted() {
            get('Building', `GetPositionViewForServiceCustomerByBuildingId/${this.buildingId}`)
                .then((x) => {
                    this.parts = x.data.parts;
                    this.positions = x.data.partPositions;

                    this.$nextTick(() => {
                        if (this.parts.length > 0) {
                            this.tabIndex = 0;
                            this.currentPart = this.parts[0];

                            this.isLoadingParts = false;
                        }
                    });
                });
        },
        methods: {
            onScrollInTabs() {
                this.setScrollingIcons('tabs', 'tabScrollingIcons');
            },
            setScrollingIcons(ref, obj) {
                if (typeof this.$refs[ref] === 'undefined')
                    return;
                let el = this.$refs[ref].$el;

                const firstChild = el.querySelector('li:first-child');
                const isFirstChildVisible = isInViewport(firstChild);
                this[obj].left = !isFirstChildVisible;

                const lastChild = el.querySelector('li:last-child');
                const isLastChildVisible = isInViewport(lastChild);
                this[obj].right = !isLastChildVisible;
            },
            onResize() {
                this.setScrollingIcons('tabs', 'tabScrollingIcons');
            },
            getNumberOfRows(partId) {
                return this.positions.reduce((number, x) => {
                    return x.partId === partId ? number + 1 : number;
                }, 0);
            },
            async onZoomIn() {
                this.closeAllPopovers();

                const panZoomComponent = this.getCurrentPanZoom();
                const instance = this.panZoomInstance;

                if (!instance || !panZoomComponent) {
                    console.warn('PanZoom inte redo');
                    return;
                }

                const container = panZoomComponent.$el;
                if (!container)
                    return;

                const currentZoom = instance.getZoom
                    ? instance.getZoom()
                    : 1;
                const targetZoom = currentZoom * 1.5;

                const centerX = this.boxSize.width / 2;
                const centerY = this.boxSize.height / 2;

                instance.smoothZoom(centerX, centerY, targetZoom);
                
            },
            async onZoomOut() {
                this.closeAllPopovers();

                const panZoomComponent = this.getCurrentPanZoom();
                const instance = this.panZoomInstance;

                if (!instance || !panZoomComponent)
                    return;

                const container = panZoomComponent.$el;
                if (!container)
                    return;

                const currentZoom = instance.getZoom
                    ? instance.getZoom()
                    : 1;
                const targetZoom = currentZoom * 0.75;

                const centerX = this.boxSize.width / 2;
                const centerY = this.boxSize.height / 2;

                instance.smoothZoom(centerX, centerY, targetZoom);
            },
            getCurrentPanZoom() {
                if (!this.currentPart?.id)
                    return null;
                const refName = `panZoom-${this.currentPart.id}`;
                const component = this.$refs[refName];

                return Array.isArray(component)
                    ? component[0]
                    : component;
            },
            onPanZoomInit(panzoomInstance) {
                this.panZoomInstance = panzoomInstance;

                const comp = this.getCurrentPanZoom();
                if (comp) {
                    comp.panZoomInstance = panzoomInstance;
                }
            },
            onPanStart() {
                this.disableAllPopovers();
                this.isPanning = true;
            },
            onPanEnd() {
                this.enableAllPopovers();
                this.isPanning = false;
            },
            getUrl(id) {
                return getDownloadUrlByArticleId(id);
            },
            closeAllPopovers() {
                this.$root.$emit('bv::hide::popover');
            },
            enableAllPopovers() {
                this.$root.$emit('bv::enable::popover');
            },
            disableAllPopovers() {
                this.closeAllPopovers();
                this.$root.$emit('bv::disable::popover');
            },
            changePositionScale(change) {
                let scale = Math.round((this.symbolSize + change) * 10) / 10
                if (scale <= 0)
                    scale = 0.1;
                if (scale > 3)
                    scale = 3;

                this.symbolSize = scale;
            },
        },
        created() {
            window.addEventListener('resize', this.onResize);
        },
        destroyed() {
            window.removeEventListener('resize', this.onResize);
        },
        watch: {
            tabIndex(newIndex) {
                if (this.parts[newIndex]) {
                    this.currentPart = this.parts[newIndex];
                    this.panZoomInstance = null;
                }
            }
        }
    };
</script>

<style scoped>
    .drawing--actions-buttons-group-top {
        z-index: 10;
        position: absolute;
    }

    .drawing--actions-buttons-group-side {
        z-index: 10;
        position: absolute;
        margin-top: 36px;
        width: 20px;
    }

    g:focus {
        outline: none;
    }
</style>